import { facturaDialogSx } from "../../facturasStyles";
import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Divider,
  Paper,
} from "@mui/material";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningIcon from "@mui/icons-material/Warning";
import DescriptionIcon from "@mui/icons-material/Description";
import ScheduleIcon from "@mui/icons-material/Schedule";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  light: "#4caf50",
  white: "#ffffff",
  gray: "#f5f5f5",
  lightGray: "#e0e0e0",
  darkGray: "#333333",
  error: "#d32f2f",
  success: "#2e7d32",
  info: "#0b4f6c",
};

const ConfirmacionFacturas = ({
  open,
  onClose,
  importing,
  importResult,
  fileName,
  importProgress = 0,
}) => {
  if (importing) {
    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...facturaDialogSx,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          },
        }}
        disableEscapeKeyDown
        disableBackdropClick
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            color: colors.white,
            py: 3,
            px: 4,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={28} sx={{ color: colors.white }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: colors.white }}>
                Procesando Facturas
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                Por favor espere mientras se procesa el archivo
              </MDTypography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 5, px: 4, textAlign: "center" }}>
          <Box position="relative" display="inline-block" mb={3}>
            <Box
              position="absolute"
              top="50%"
              left="50%"
              sx={{ transform: "translate(-50%, -50%)" }}
            >
              <CloudUploadIcon sx={{ fontSize: 38, color: colors.secondary, opacity: 0.8 }} />
            </Box>
          </Box>

          <MDTypography variant="h5" fontWeight="bold" gutterBottom sx={{ color: colors.darkGray }}>
            Procesando archivo...
          </MDTypography>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              px: 3,
              py: 1,
              bgcolor: colors.gray,
              borderRadius: 3,
              mt: 2,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 20, color: colors.secondary }} />
            <MDTypography variant="body1" sx={{ color: "text.secondary" }}>
              {fileName || "Archivo Excel"}
            </MDTypography>
          </Box>

          <Box sx={{ width: "100%", mt: 4 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <MDTypography variant="caption" sx={{ color: "text.secondary" }}>
                Progreso
              </MDTypography>
              <MDTypography variant="caption" fontWeight="bold" sx={{ color: colors.secondary }}>
                {Math.round(importProgress)}%
              </MDTypography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={importProgress}
              sx={{
                height: 8,
                borderRadius: 3,
                backgroundColor: `${colors.light}30`,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: colors.secondary,
                  borderRadius: 3,
                },
              }}
            />
          </Box>

          <MDTypography variant="body2" sx={{ color: "text.secondary", mt: 3 }}>
            {importProgress > 0 && importProgress < 100
              ? `Procesando facturas... ${Math.round(importProgress)}%`
              : "El procesamiento puede tomar unos segundos"}
          </MDTypography>
        </DialogContent>
      </Dialog>
    );
  }

  if (importResult?.success) {
    const hasValidCount = importResult.importedCount && importResult.importedCount > 0;
    const displayCount = hasValidCount ? importResult.importedCount : null;

    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...facturaDialogSx,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.secondary} 100%)`,
            color: colors.white,
            py: 3,
            px: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircleIcon sx={{ fontSize: 28 }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: colors.white }}>
                Procesamiento Exitoso
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                Las facturas se han procesado correctamente
              </MDTypography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: colors.white }}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                display: "inline-flex",
                p: 2.5,
                borderRadius: "50%",
                bgcolor: `${colors.success}10`,
                mt: 1,
                mb: 2,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 80, color: colors.success }} />
            </Box>
            <MDTypography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{ color: colors.success }}
            >
              ¡Completado!
            </MDTypography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: colors.gray,
              borderRadius: 3,
            }}
          >
            <MDTypography
              variant="subtitle1"
              fontWeight="bold"
              mb={2}
              sx={{ color: colors.primary }}
            >
              Resumen del procesamiento
            </MDTypography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <DescriptionIcon sx={{ fontSize: 22, color: colors.secondary }} />
                <MDTypography variant="body1" sx={{ color: "text.secondary" }}>
                  Archivo:
                </MDTypography>
              </Box>
              <MDTypography variant="body1" fontWeight="medium" sx={{ color: colors.darkGray }}>
                {fileName || "desconocido"}
              </MDTypography>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <CheckCircleIcon sx={{ fontSize: 22, color: colors.success }} />
                <MDTypography variant="body1" fontWeight="bold" sx={{ color: colors.success }}>
                  Estado:
                </MDTypography>
              </Box>
              <MDTypography variant="body1" fontWeight="bold" sx={{ color: colors.success }}>
                Procesado correctamente
              </MDTypography>
            </Box>
          </Paper>

          {displayCount !== null && displayCount > 0 && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "#e8f5e9",
                borderRadius: 3,
                textAlign: "center",
              }}
            ></Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            justifyContent: "center",
            bgcolor: colors.gray,
            borderTop: `1px solid ${colors.lightGray}`,
          }}
        >
          <MDButton
            variant="contained"
            onClick={onClose}
            size="medium"
            sx={{
              bgcolor: colors.success,
              color: colors.white,
              px: 5,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              "&:hover": {
                bgcolor: colors.secondary,
              },
            }}
          >
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  if (importResult?.success === false && importResult) {
    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...facturaDialogSx,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.error} 0%, #c62828 100%)`,
            color: colors.white,
            py: 3,
            px: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <ErrorIcon sx={{ fontSize: 28 }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: colors.white }}>
                Error en el Procesamiento
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                No se pudo procesar el archivo
              </MDTypography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: colors.white }}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                display: "inline-flex",
                p: 2.5,
                borderRadius: "50%",
                bgcolor: `${colors.error}10`,
                mt: 1,
                mb: 2,
              }}
            >
              <ErrorIcon sx={{ fontSize: 80, color: colors.error }} />
            </Box>

            <MDTypography variant="h4" fontWeight="bold" gutterBottom sx={{ color: colors.error }}>
              ¡Algo salió mal!
            </MDTypography>

            <Paper
              elevation={0}
              sx={{
                bgcolor: "#ffebee",
                borderRadius: 3,
                p: 2.5,
                mt: 2,
                border: `1px solid #ffcdd2`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <WarningIcon sx={{ fontSize: 22, color: colors.error }} />
                <MDTypography variant="body1" fontWeight="medium" sx={{ color: colors.error }}>
                  {importResult.error || "Error al procesar el archivo"}
                </MDTypography>
              </Box>
            </Paper>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: colors.gray,
              borderRadius: 3,
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <ScheduleIcon sx={{ fontSize: 22, color: colors.info }} />
              <MDTypography variant="subtitle1" fontWeight="bold" sx={{ color: colors.primary }}>
                Posibles soluciones:
              </MDTypography>
            </Box>
            <Box component="ul" sx={{ margin: 0, paddingLeft: "1.75rem" }}>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" sx={{ color: "text.secondary" }}>
                  Verifique que el archivo sea de tipo <strong>.xlsx</strong> o{" "}
                  <strong>.xls</strong>
                </MDTypography>
              </li>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" sx={{ color: "text.secondary" }}>
                  Asegúrese que el archivo contenga las columnas:{" "}
                  <strong># Factura, NIT, Empresa, Valor, Código</strong>
                </MDTypography>
              </li>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" sx={{ color: "text.secondary" }}>
                  Verifique que todas las filas tengan datos completos
                </MDTypography>
              </li>
              <li>
                <MDTypography variant="body2" sx={{ color: "text.secondary" }}>
                  Intente nuevamente en unos momentos
                </MDTypography>
              </li>
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            justifyContent: "center",
            bgcolor: colors.gray,
            borderTop: `1px solid ${colors.lightGray}`,
          }}
        >
          <MDButton
            variant="contained"
            onClick={onClose}
            size="medium"
            sx={{
              bgcolor: colors.error,
              color: colors.white,
              px: 5,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              "&:hover": {
                bgcolor: "#c62828",
              },
            }}
          >
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
};

ConfirmacionFacturas.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  importing: PropTypes.bool,
  importResult: PropTypes.shape({
    success: PropTypes.bool,
    message: PropTypes.string,
    error: PropTypes.string,
    importedCount: PropTypes.number,
  }),
  fileName: PropTypes.string,
  importProgress: PropTypes.number,
};

ConfirmacionFacturas.defaultProps = {
  importing: false,
  importResult: null,
  fileName: "",
  importProgress: 0,
};

export default ConfirmacionFacturas;
